<template>
  <v-card outlined class="rounded-lg fill-height">
    <v-card-text class="pa-4">

      <!-- Header -->
      <div class="d-flex align-center mb-3">
        <v-avatar size="40" color="primary" class="mr-3" rounded>
          <v-icon color="white">mdi-eye-off-outline</v-icon>
        </v-avatar>
        <div>
          <div class="subtitle-1 font-weight-bold">Feedback For</div>
          <div class="caption grey--text">Share confidentially &amp; anonymously</div>
        </div>
      </div>

      <div class="caption grey--text mb-4">
        Your identity will not be recorded. Share suggestions, concerns, or ideas.
      </div>

      <!-- Team first -->
      <div class="body-2 font-weight-medium mb-1">Team</div>
      <v-autocomplete
        v-model="form.role_id"
        :items="roleOptions"
        item-text="text"
        item-value="value"
        placeholder="Select a team first..."
        dense
        outlined
        hide-details
        clearable
        class="mb-4 rounded-lg"
      />

      <!-- To field — enabled only after a team is selected -->
      <div class="body-2 font-weight-medium mb-1">To (Manager or Colleague)</div>
      <v-autocomplete
        v-model="form.recipient_id"
        :items="filteredUserOptions"
        item-text="text"
        item-value="value"
        placeholder="Search member..."
        dense
        outlined
        hide-details
        clearable
        class="mb-4 rounded-lg"
      />

      <!-- Category field -->
      <div class="body-2 font-weight-medium mb-1">Category</div>
      <v-select
        v-model="form.category"
        :items="categories"
        dense
        outlined
        hide-details
        class="mb-4 rounded-lg"
      />

      <!-- Feedback textarea -->
      <div class="body-2 font-weight-medium mb-1">Your Feedback</div>
      <v-textarea
        v-model="form.message"
        placeholder="Type your feedback here..."
        dense
        outlined
        hide-details
        rows="4"
        no-resize
        class="mb-4 rounded-lg"
      />

      <!-- Send button -->
      <v-btn
        color="primary"
        depressed
        block
        large
        class="white--text text-capitalize font-weight-bold rounded-lg"
        style="letter-spacing:0.02em;"
        :loading="sending"
        @click="send"
      >
        <v-icon class="mr-2">mdi-send</v-icon>
        Send Anonymously
      </v-btn>

    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'FeedbackSendCard',
  inject: {
    users:       { default: () => () => [] },
    roles:       { default: () => () => [] },
    allManagers: { default: () => () => [] },
  },
  data() {
    return {
      sending: false,
      form: { recipient_id: null, role_id: null, category: 'General', message: '', is_anonymous: true },
      categories: ['General', 'Process', 'Team Dynamics', 'Leadership', 'Tools & Resources', 'Workload'],
    };
  },
  watch: {
    'form.role_id'() {
      // Clear recipient when team changes so user picks a valid member
      this.form.recipient_id = null;
    },
  },
  computed: {
    roleOptions() {
      const list = typeof this.roles === 'function' ? this.roles() : (this.roles || []);
      return (Array.isArray(list) ? list : []).map(r => ({ text: r.name, value: r.id }));
    },
    // Filter users directly by their role_id; show everyone when no team is selected
    filteredUserOptions() {
      const allUsers = (typeof this.users === 'function' ? this.users() : this.users) || [];
      const list = this.form.role_id
        ? allUsers.filter(u => u && u.role_id === this.form.role_id)
        : allUsers.filter(u => u);
      return list
        .map(u => ({ text: u.name, value: u.id }))
        .sort((a, b) => (a.text || '').localeCompare(b.text || ''));
    },
  },
  methods: {
    async send() {
      if (!this.form.role_id) { this.$toast && this.$toast.error('Select a team first'); return; }
      if (!this.form.recipient_id) { this.$toast && this.$toast.error('Pick a recipient'); return; }
      if (!this.form.message.trim()) { this.$toast && this.$toast.error('Add your feedback'); return; }
      this.sending = true;
      try {
        const payload = {
          recipient_id: this.form.recipient_id,
          category: this.form.category,
          message: this.form.message,
          type: 'anonymous',
          is_anonymous: true,
        };
        if (this.form.role_id) payload.role_id = this.form.role_id;

        const res = await this.$axios.post('api/employee-growth/feedback', payload);
        if (res.data.response) {
          this.form = { recipient_id: null, role_id: null, category: 'General', message: '', is_anonymous: true };
          this.$toast && this.$toast.success('Anonymous feedback sent!');
          // Let other growth cards (Sent tab, inbox badge) refresh.
          this.$root.$emit('growth:feedback:sent');
          this.$root.$emit('growth:refresh');
        }
      } catch (e) {
        this.$toast && this.$toast.error('Failed to send feedback');
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>
